<template lang="pug">
  div(
    ref="fieldWrapper"
    :id="field.key"
    class="dynamic-field-wrapper"
    :class="wrapperClasses"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @drop="onDrop"
    @dragover="onDragOver"
    v-on="wrapperTypeRelatedListeners[field.type] ? wrapperTypeRelatedListeners[field.type] : null"
    v-click-outside="handleClickOutside"
  )
    templates-dynamic-toolbar(
      v-if="showTemplateToolbar"
      v-bind="toolbarTypeRelatedProps[field.type] ? toolbarTypeRelatedProps[field.type] : null"
      :field="field"
      :fontSize.sync="fontSize"
      @duplicate="emitFieldData('duplicate')"
      @copy="emitFieldData('copy')"
      @cut="emitFieldData('cut')"
      @delete="emitFieldData('delete')"
      @onError="hasError = $event"
      @toggleAutocomplete="toggleAutocomplete"
      @toggleRequired="toggleRequired"
    )
    block-document-field-toolbar(v-if="showDocumentToolbar")
    template(v-if="showRawValues")
      templates-field-value(:field="field")
    template(v-else)
      component(
        ref="dynamicComponent"
        :is="component"
        :field="field"
        :value.sync="documentValue"
        :can-edit="canEdit"
        :is-disabled="isDisabled"
        :font-size="fontSize"
        :is-resizing="isResizing"
        :has-error="hasError"
        :draggable="isDraggable"
        :placeholder="placeholder"
        v-on="componentTypeRelatedListeners[field.type] ? componentTypeRelatedListeners[field.type] : null"
        @copy="emitFieldData('copy')"
        @cut="emitFieldData('cut')"
        @delete="emitFieldData('delete')"
        @guideNextStep="goToNextStep"
        @blur="onHintChange"
      )
    e-guide-item(
      v-if="guideVisible"
      :step="step"
      :steps-count="stepsCount"
      :is-last-step="step === stepsCount"
      :left="showGuideOnLeft"
      @next="goToNextStep"
    )
    templates-inputs-resizer(
      v-if="canEdit && $refs.fieldWrapper && isCollapsed"
      :field="field"
      :element="$refs.fieldWrapper"
      @resize="setResizeState"
      v-bind="resizerTypeRelatedProps[field.type] ? resizerTypeRelatedProps[field.type] : null"
    )
</template>

<script>
import { mapGetters } from 'vuex'
import { ClickOutside } from 'vuetify/lib/directives'
import TemplatesDynamicToolbar from '~/modules/templates/components/inputs/templates-dynamic-toolbar'
import TemplatesDynamicTextarea from '~/modules/templates/components/inputs/templates-dynamic-textarea'
import TemplatesDynamicDateInput from '~/modules/templates/components/inputs/templates-dynamic-date-input'
import TemplatesDynamicCheckbox from '~/modules/templates/components/inputs/templates-dynamic-checkbox'
import TemplatesDynamicNumeric from '~/modules/templates/components/inputs/templates-dynamic-numeric'
import TemplatesDynamicSelect from '~/modules/templates/components/inputs/templates-dynamic-select'
import TemplatesDynamicFormula from '~/modules/templates/components/inputs/templates-dynamic-formula'
import TemplatesDynamicSignature from '~/modules/templates/components/inputs/templates-dynamic-signature'
import TemplatesDynamicImage from '~/modules/templates/components/inputs/templates-dynamic-image'
import EGuideItem from '~/modules/templates/components/elements/e-guide-item'
import TemplatesInputsResizer from '~/modules/templates/components/inputs/templates-inputs-resizer'
import TemplatesFieldValue from '~/modules/templates/components/inputs/templates-field-value'
import responsive from '~/mixins/pages/responsive'
import TemplatesDynamicSwitch from '~/modules/templates/components/inputs/templates-dynamic-switch'
import TemplatesDynamicAutocomplete from '~/modules/templates/components/inputs/templates-dynamic-autocomplete'
import { TYPES, STATUSES } from '~/modules/templates/constants/fields'
import BlockDocumentFieldToolbar from '~/modules/documents/components/blocks/block-document-field-toolbar.vue'

export default {
  name: 'TemplatesDynamicField',
  components: {
    BlockDocumentFieldToolbar,
    TemplatesDynamicSelect,
    TemplatesDynamicCheckbox,
    TemplatesDynamicDateInput,
    TemplatesDynamicTextarea,
    TemplatesDynamicNumeric,
    TemplatesDynamicFormula,
    TemplatesDynamicSignature,
    TemplatesDynamicToolbar,
    TemplatesInputsResizer,
    EGuideItem,
    TemplatesFieldValue,
    TemplatesDynamicSwitch,
    TemplatesDynamicAutocomplete,
    TemplatesDynamicImage
  },
  directives: {
    ClickOutside
  },
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      default: () => ({})
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isTemplate: {
      type: Boolean,
      default: false
    },
    step: {
      type: Number,
      default: null
    },
    stepsCount: {
      type: Number,
      default: null
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    showRawValues: {
      type: Boolean,
      default: false
    },
    isLargeDocument: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      guideVisible: false,
      guideResolver: null,
      isResizing: false,
      fontSize: null,
      wasOpened: false,
      wasClosed: false,
      documentValue: null,
      hasError: false,
      showGuideOnLeft: false
    }
  },
  computed: {
    ...mapGetters('documents', {
      documentScale: 'documentScale'
    }),
    ...mapGetters('templatefields', {
      fields: 'fields',
      toolbarsMap: 'toolbarsMap',
      filledFields: 'filledFields',
      isDraggingMap: 'isDraggingMap',
      activeFieldId: 'activeFieldId',
      activeFieldOffsetX: 'activeFieldOffsetX',
      activeFieldOffsetY: 'activeFieldOffsetY'
    }),
    isFilled() {
      if (this.field.type === TYPES.checkbox) {
        return ['checked', true].includes(this.documentValue)
      }
      return !!this.documentValue || !!this.field.documentFieldValue
    },
    showTemplateToolbar() {
      return this.toolbarsMap[this.field.id] && !this.isDraggingMap[this.field.id] && this.canEdit
    },
    showDocumentToolbar() {
      return (
        this.toolbarsMap[this.field.id] &&
        !this.isDraggingMap[this.field.id] &&
        this.field.required &&
        !this.canEdit
      )
    },
    isActive() {
      return this.toolbarsMap[this.field.id] || this.guideVisible
    },
    wrapperClasses() {
      return {
        active: this.isActive,
        disabled: this.isDisabled && !this.canEdit,
        checkbox: this.field.type === TYPES.checkbox && !this.showRawValues,
        'checkbox--error': this.field.type === TYPES.checkbox && this.hasError,
        checked: this.field.type === TYPES.checkbox && !!this.hasValue && !this.showRawValues
      }
    },
    hasValue() {
      return this._.find(
        this.filledFields,
        filledField =>
          filledField.id === this.field.id || filledField.id === this.field.documentFieldId
      )
    },
    isDraggable() {
      return this.canEdit && !this.isResizing
    },
    componentsTypeMap() {
      return {
        text: 'templates-dynamic-textarea',
        date: 'templates-dynamic-date-input',
        checkbox: 'templates-dynamic-checkbox',
        select: 'templates-dynamic-select',
        numeric: 'templates-dynamic-numeric',
        formula: 'templates-dynamic-formula',
        signature: 'templates-dynamic-signature',
        switch: 'templates-dynamic-switch',
        autocomplete: 'templates-dynamic-autocomplete',
        image: 'templates-dynamic-image'
      }
    },
    component() {
      return this.componentsTypeMap[this.field.type]
    },
    wrapperTypeRelatedListeners() {
      return {
        checkbox: {
          mousedown: this.onFieldWrapperMousedown
        },
        switch: {
          mousedown: this.onFieldWrapperMousedown
        },
        image: {
          mousedown: this.onFieldWrapperMousedown
        }
      }
    },
    toolbarTypeRelatedProps() {
      return {
        checkbox: {
          showFontSizeInput: false
        }
      }
    },
    componentTypeRelatedListeners() {
      return {
        select: {
          wasOpened: this.saveOpenedState
        },
        formula: {
          wasOpened: this.saveOpenedState
        }
      }
    },
    resizerTypeRelatedProps() {
      return {
        checkbox: {
          small: true
        }
      }
    },
    isCollapsed() {
      if (
        this.field.type === TYPES.select ||
        this.field.type === TYPES.formula ||
        this.field.type === TYPES.autocomplete
      ) {
        return !this.toolbarsMap[this.field.id]
      }
      return true
    },
    filledFieldId() {
      return this.isTemplate ? this.field.id : this.field.documentFieldId
    },
    placeholder() {
      if (!this.canEdit) {
        return this.field.required ? this.field.hint + '*' : this.field.hint
      }
      return this.field.hint
    }
  },
  watch: {
    field: {
      deep: true,
      handler(val, prevVal) {
        if (val.positionX !== prevVal.positionX || val.positionY !== prevVal.positionY) {
          this.onChangeFieldPosition()
        }
        // set field value from quick fill form
        if (val.quickValue !== prevVal?.quickValue) {
          this.documentValue = val.quickValue
        }
        if (val.width !== prevVal?.width) {
          this.setFieldWrapperProperties()
        }
      }
    },
    fontSize(val) {
      const fontSize = typeof val === 'number' ? val : Number(val)
      if (fontSize !== this.field.textSize) {
        this.setFontSize(val)
      }
    },
    // custom validation happens on extended menu (select, formula) collapse and click 'save' button
    wasClosed(val) {
      if (val) {
        this.wasOpened = false
        this.validate()
      }
    },
    documentValue(val) {
      if (!this.canEdit) {
        if (val) {
          // TODO: BAD APPROACH
          if (this.field.type === TYPES.checkbox && val === 'unchecked') {
            this.$store.commit('templatefields/REMOVE_FILLED_FIELD', this.filledFieldId)
          } else {
            this.$store.commit('templatefields/SET_FILLED_FIELD', {
              id: this.filledFieldId,
              value: val
            })
          }
          this.goToNextStep()
        } else {
          this.$store.commit('templatefields/REMOVE_FILLED_FIELD', this.filledFieldId)
        }
      }
    },
    hasError(val) {
      if (val) {
        this.$store.commit('templatefields/SET_ERROR_FIELD_IDS', this.field.key)
      } else {
        this.$store.commit('templatefields/REMOVE_ERROR_FIELD_IDS', this.field.key)
      }
    }
  },
  mounted() {
    this.setFieldWrapperProperties()
    this.onChangeFieldPosition()
  },
  methods: {
    async validate() {
      const results = []
      if (this.canEdit) {
        if (this.field.type === TYPES.formula || this.field.type === TYPES.select) {
          const resultOfInnerValidation = await this.handleInnerValidation()
          results.push(resultOfInnerValidation)
        }
        const isValidCommonErrors = !this.hasError
        results.push(isValidCommonErrors)
      } else {
        let isValid
        if (this.field.required) {
          isValid = !!this.hasValue
        } else {
          isValid = true
        }
        this.hasError = !isValid
        results.push(isValid)
      }
      return this._.some(results, validationResult => !validationResult)
    },
    async handleInnerValidation() {
      return await this.$refs.dynamicComponent.validate()
    },
    saveOpenedState(fieldId) {
      if (this.field.id === fieldId) {
        this.wasClosed = false
        this.wasOpened = true
      }
    },
    setResizeState(val) {
      this.isResizing = val
    },
    emitFieldData(eventName) {
      this.$emit(eventName, this.field)
    },
    onDragStart(e) {
      this.$store.commit('templatefields/RESET_ALL_TOOLBARS_EXCEPT_ACTIVE', this.field.id)
      this.$store.commit('templatefields/SET_DRAGGING_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', this.field.id)
      const rect = this.$refs.fieldWrapper.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top
      this.$emit('startDrag', offsetX, offsetY)
    },
    onDragEnd(e) {
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', null)
    },
    onDrop(e) {
      const pages = document.querySelectorAll('canvas')
      const pageRect = pages[this.field.page - 1].getBoundingClientRect()
      const transferedFieldType = e.dataTransfer.getData('text')
      if (this.activeFieldId) {
        // fields being transfered inside own borders or other field
        this.$store.commit('templatefields/SET_DRAGGING_MAP_FIELD_ID', {
          id: this.activeFieldId,
          value: false
        })
        const field = this._.find(this.fields, field => field.id === this.activeFieldId)
        this.$store.commit('templatefields/UPDATE_FIELD', {
          id: this.activeFieldId,
          updatedData: {
            positionX: e.clientX - this.activeFieldOffsetX - pageRect.left,
            positionY: e.clientY - this.activeFieldOffsetY - pageRect.top,
            changed: true,
            changedPosition: true,
            page: this.field.page,
            changedPage: this.field.page !== field.page
          }
        })
        this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', null)
        return
      }
      // new fields from side panel on existing field
      if (transferedFieldType) {
        const fieldOverrides = {
          positionX: e.clientX - pageRect.left,
          positionY: e.clientY - pageRect.top
        }
        this.$store.dispatch('templatefields/createField', {
          type: transferedFieldType,
          page: this.field.page,
          overrides: fieldOverrides
        })
      }
    },
    onDragOver(e) {
      e.preventDefault()
    },
    onFieldWrapperMousedown(e) {
      e.stopPropagation()
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', this.field.id)
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
    },
    handleClickOutside() {
      if (this.wasOpened) {
        this.wasClosed = true
      }
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: false
      })
      this.$store.commit('templatefields/SET_LAST_ACTIVE_FIELD_ID', this.field.id)
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', null)
    },
    getPageDimensions() {
      const currentPageWidth = document.querySelector('canvas').offsetWidth
      const currentPageHeight = document.querySelector('canvas').offsetHeight
      const desktopPageWidth = document.querySelector('canvas').width
      const desktopPageHeight = document.querySelector('canvas').height

      return {
        currentPageWidth,
        currentPageHeight,
        desktopPageWidth,
        desktopPageHeight
      }
    },
    onChangeFieldPosition() {
      if (this.xsDevice || this.smDevice || this.mdDevice) {
        this.$nextTick(() => {
          const { currentPageWidth, currentPageHeight, desktopPageWidth, desktopPageHeight } =
            this.getPageDimensions()
          this.$refs.fieldWrapper.style.left = this.countMobileFieldPosition(
            desktopPageWidth,
            currentPageWidth,
            this.field.positionX * this.documentScale
          )
          this.$refs.fieldWrapper.style.top = this.countMobileFieldPosition(
            desktopPageHeight,
            currentPageHeight,
            this.field.positionY * this.documentScale
          )
        })
      } else if ((this.field.status !== STATUSES.new && !this.field.changed) || !this.isTemplate) {
        this.$refs.fieldWrapper.style.left =
          Math.floor(this.field.positionX * this.documentScale) + 'px'
        this.$refs.fieldWrapper.style.top =
          Math.floor(this.field.positionY * this.documentScale) + 'px'
      } else {
        this.$refs.fieldWrapper.style.left = this.field.positionX + 'px'
        this.$refs.fieldWrapper.style.top = this.field.positionY + 'px'
      }
    },
    countMobileFieldPosition(desktopPageDimension, mobilePageDimension, desktopFieldPosition) {
      const newPosition = (mobilePageDimension / desktopPageDimension) * desktopFieldPosition
      return newPosition + 'px'
    },
    setFieldWrapperProperties() {
      // fields saved on backend
      const isCreatedField = this.field.status === STATUSES.created && !this.field.changed
      // method triggers if we moved created to new page, as each page has its own fields wrapper
      const isCreatedFieldMovedToOtherPage =
        this.field.status === STATUSES.created && this.field.changedPage
      // on Document, we can`t manipulate field parameters, so we are just getting them from backend
      const isDocument = !this.isTemplate
      const shouldIncludeScaleFactor =
        isCreatedField || isCreatedFieldMovedToOtherPage || isDocument
      if (this.xsDevice || this.smDevice || this.mdDevice) {
        this.$nextTick(() => {
          const { currentPageWidth, currentPageHeight, desktopPageWidth, desktopPageHeight } =
            this.getPageDimensions()
          this.$refs.fieldWrapper.style.width = this.countMobileFieldPosition(
            desktopPageWidth,
            currentPageWidth,
            this.field.width * this.documentScale
          )
          this.$refs.fieldWrapper.style.height = this.countMobileFieldPosition(
            desktopPageHeight,
            currentPageHeight,
            this.field.height * this.documentScale
          )
        })
      } else if (shouldIncludeScaleFactor) {
        this.$refs.fieldWrapper.style.width =
          Math.floor(this.field.width * this.documentScale) + 'px'
        this.$refs.fieldWrapper.style.height =
          Math.floor(this.field.height * this.documentScale) + 'px'
      } else {
        this.$refs.fieldWrapper.style.width = this.field.width + 'px'
        this.$refs.fieldWrapper.style.height = this.field.height + 'px'
      }
      this.fontSize = this.field.textSize
    },
    goToNextStep() {
      this.guideVisible = false
      if (typeof this.guideResolver === 'function') {
        this.guideResolver()
      }
    },
    showGuide() {
      return new Promise(resolve => {
        this.$nextTick(() => {
          if (this.xsDevice) {
            const guideWidth = 70
            const fieldRect = this.$refs.fieldWrapper?.getBoundingClientRect()
            const page = document.querySelector('canvas')
            const pageRect = page.getBoundingClientRect()
            const pageSpace = pageRect.right - fieldRect.right

            this.showGuideOnLeft = pageSpace < guideWidth
          }
          this.guideVisible = !this.guideVisible
          this.guideResolver = resolve
        })
      })
    },
    setFontSize(size) {
      this.fontSize = size
      // TODO: check only field on fontsize error
      if (!this.hasError) {
        this.$store.commit('templatefields/UPDATE_FIELD', {
          id: this.field.id,
          updatedData: {
            textSize: size,
            changed: true
          }
        })
      }
    },
    toggleAutocomplete() {
      const transformToAutocomplete = this.field.type === TYPES.text
      this.$store.dispatch('templatefields/createField', {
        type: transformToAutocomplete ? TYPES.autocomplete : TYPES.text,
        page: this.field.page,
        overrides: {
          positionX:
            this.field.status === STATUSES.created
              ? Math.ceil(this.field.positionX * this.documentScale)
              : this.field.positionX,
          positionY:
            this.field.status === STATUSES.created
              ? Math.ceil(this.field.positionY * this.documentScale)
              : this.field.positionY
        }
      })
      this.emitFieldData('delete')
    },
    toggleRequired() {
      this.$store.commit('templatefields/UPDATE_FIELD', {
        id: this.field.id,
        updatedData: {
          required: !this.field.required,
          changed: true
        }
      })
    },
    onHintChange(hint) {
      if (this.canEdit) {
        if (hint !== this.field.hint) {
          this.$store.commit('templatefields/UPDATE_FIELD', {
            id: this.field.id,
            updatedData: {
              hint,
              changed: true
            }
          })
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-field-wrapper {
  position: absolute;
  z-index: 5;

  &.active {
    z-index: map-get($zIndexes, 'fixed');
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.checkbox {
    display: flex;
    align-items: center;
    justify-content: center;

    padding: 4px;

    border-radius: 6px;
    border: 1px solid $primary;
    background: rgba(255, 255, 255, 0.7);
    resize: none;

    @media (max-width: map-get($breakpoints, 'sm')) {
      border-radius: 4px;
      padding: 0;
      height: 12px !important;
      width: 12px !important;
    }

    &.checked {
      background:
        linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
        rgba(255, 255, 255, 0.4);
      color: $primary;
    }
    &.checkbox--error {
      border: 1px solid $danger;
      background: rgba(255, 23, 68, 0.12);
    }
  }
}
</style>
